import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { getAssistantMotionTransition } from './assistantMotion';

type AssistantMeasuredDisclosureProps = {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
  style?: CSSProperties;
  'data-testid'?: string;
};

export function AssistantMeasuredDisclosure({
  children,
  className,
  innerClassName,
  style,
  ...rest
}: AssistantMeasuredDisclosureProps) {
  const reducedMotion = Boolean(useReducedMotion());
  const contentRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

  useEffect(() => {
    const node = contentRef.current;
    if (!node) {
      return undefined;
    }

    const updateHeight = () => {
      const nextHeight = node.getBoundingClientRect().height;
      if (nextHeight > 0) {
        setMeasuredHeight(nextHeight);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(() => {
      updateHeight();
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [children]);

  return (
    <motion.div
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, height: 0, y: -4 }}
      animate={reducedMotion
        ? { opacity: 1, height: measuredHeight ?? 'auto' }
        : { opacity: 1, height: measuredHeight ?? 'auto', y: 0 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, height: 0, y: -4 }}
      transition={getAssistantMotionTransition('disclosure', reducedMotion)}
      className={className}
      style={{
        overflow: 'hidden',
        ...style,
      }}
      {...rest}
    >
      <div ref={contentRef} className={innerClassName}>
        {children}
      </div>
    </motion.div>
  );
}
